import type {
  DevEvalParams,
  DevEvalResult,
  GetSnippetParams,
  QueryRangeParams,
  WordAddInApi,
} from "@shared/lib/rpc/apis/word-add-in-api";
import type { SyncEngine } from "../sync-engine/SyncEngine";
import { downloadDocx } from "../downloadDocx";
import { GetSurfaceContextOperation } from "./operations/GetSurfaceContextOperation";
import { GetSnippetOperation } from "./operations/GetSnippetOperation";
import { SetSnippetOperation } from "./operations/SetSnippetOperation";
import { QueryRangeOperation } from "./operations/QueryRangeOperation";
import { getTrackedRange } from "./TrackedRangeStore";
import { storeConversationId } from "./conversation-storage";

export interface WordAddInApiDependencies {
  syncEngine: SyncEngine;
  onSwitchMode?: (mode: "chat" | "automation") => void;
}

/**
 * Handles all RPC calls from the chat iframe to the Word add-in.
 *
 * Dependencies are injected via constructor for testability.
 */
export class WordAddInApiHandler implements WordAddInApi {
  private readonly syncEngine: SyncEngine;
  private readonly onSwitchMode?: (mode: "chat" | "automation") => void;

  constructor(deps: WordAddInApiDependencies) {
    this.syncEngine = deps.syncEngine;
    this.onSwitchMode = deps.onSwitchMode;
  }

  async getDocument() {
    const downloaded = await downloadDocx();
    return {
      base64: downloaded.base64,
      filename: downloaded.properties?.filename || "document.docx",
    };
  }

  async getSurfaceContext() {
    return this.syncEngine.perform(new GetSurfaceContextOperation());
  }

  async insertOoxmlAtPosition({
    ooxml,
    position,
  }: {
    ooxml: string;
    position: "documentStart" | "documentEnd" | "selectionReplace" | "beforeSelection" | "afterSelection";
  }): Promise<void> {
    await Word.run(async (context) => {
      switch (position) {
        case "documentStart":
          context.document.body.insertOoxml(ooxml, Word.InsertLocation.start);
          break;
        case "documentEnd":
          context.document.body.insertOoxml(ooxml, Word.InsertLocation.end);
          break;
        case "selectionReplace":
          context.document.getSelection().insertOoxml(ooxml, Word.InsertLocation.replace);
          break;
        case "beforeSelection":
          context.document.getSelection().insertOoxml(ooxml, Word.InsertLocation.before);
          break;
        case "afterSelection":
          context.document.getSelection().insertOoxml(ooxml, Word.InsertLocation.after);
          break;
      }

      await context.sync();
    });
  }

  async switchMode({ mode }: { mode: "chat" | "automation" }) {
    this.onSwitchMode?.(mode);
  }

  async getSnippet(params: GetSnippetParams) {
    return this.syncEngine.perform(new GetSnippetOperation(params));
  }

  async setSnippet({ ooxml, trackedRangeId }: { ooxml: string; trackedRangeId: string }) {
    return this.syncEngine.perform(new SetSnippetOperation({ ooxml, trackedRangeId }));
  }

  async findAndSelect({ searchTexts }: { searchTexts: string[] }): Promise<{ found: boolean }> {
    if (searchTexts.length === 0) {
      return { found: false };
    }

    // Word's search API has a 255 character limit. Truncate at word boundary.
    const MAX_SEARCH_LENGTH = 200;
    const truncateSearch = (text: string): string => {
      if (text.length <= MAX_SEARCH_LENGTH) return text;
      let truncated = text.substring(0, MAX_SEARCH_LENGTH);
      const lastSpace = truncated.lastIndexOf(" ");
      if (lastSpace > MAX_SEARCH_LENGTH / 2) {
        truncated = truncated.substring(0, lastSpace);
      }
      return truncated;
    };

    // Collect all annotation IDs across all searches for cleanup
    const allAnnotationIds: string[] = [];
    let anyFound = false;
    let lastSearchText: string | null = null;

    // Process each search text
    for (const searchText of searchTexts) {
      const truncatedSearch = truncateSearch(searchText);

      await Word.run(async (context) => {
        const results = context.document.body.search(truncatedSearch, {
          matchCase: false,
          matchWholeWord: false,
        });

        results.load("items");
        await context.sync();

        if (results.items.length === 0) {
          return;
        }

        anyFound = true;
        lastSearchText = truncatedSearch;
        const firstMatch = results.items[0];

        // Get the containing paragraph for the critique annotation
        const paragraph = firstMatch.paragraphs.getFirst();
        paragraph.load("text");
        await context.sync();

        // Annotate the entire paragraph (citations reference whole paragraphs)
        const paragraphText = paragraph.text;
        if (paragraphText.length > 0) {
          const critique: Word.Critique = {
            colorScheme: Word.CritiqueColorScheme.blue,
            start: 0,
            length: paragraphText.length,
            popupOptions: {
              brandingTextResourceId: "",
              subtitleResourceId: "",
              titleResourceId: "",
              suggestions: [],
            },
          };

          const annotationSet: Word.AnnotationSet = {
            critiques: [critique],
          };

          const annotationIds = paragraph.insertAnnotations(annotationSet);
          await context.sync();

          // Collect annotation IDs for cleanup
          allAnnotationIds.push(...annotationIds.value);
        }
      });
    }

    // Select the last match to scroll viewport there
    if (lastSearchText) {
      await Word.run(async (context) => {
        const results = context.document.body.search(lastSearchText!, {
          matchCase: false,
          matchWholeWord: false,
        });
        results.load("items");
        await context.sync();

        if (results.items.length > 0) {
          const lastMatch = results.items[0];
          // Get end of the match and place cursor there
          const endRange = lastMatch.getRange("End");
          endRange.select();
          await context.sync();
        }
      });
    }

    // Remove all annotations after 10 seconds
    if (allAnnotationIds.length > 0) {
      setTimeout(async () => {
        try {
          await Word.run(async (ctx) => {
            for (const id of allAnnotationIds) {
              try {
                const annotation = ctx.document.getAnnotationById(id);
                annotation.delete();
              } catch {
                // Individual annotation may already be gone
              }
            }
            await ctx.sync();
          });
        } catch {
          // Ignore errors during cleanup - document may have changed
        }
      }, 10000);
    }

    return { found: anyFound };
  }

  async replaceDocumentBody({ ooxml }: { ooxml: string }): Promise<void> {
    await Word.run(async (context) => {
      // Replace entire document body with the OOXML content
      context.document.body.insertOoxml(ooxml, Word.InsertLocation.replace);
      await context.sync();
    });
  }

  async replaceDocument({ base64Docx }: { base64Docx: string }): Promise<void> {
    await Word.run(async (context) => {
      // Replace entire document with base64-encoded DOCX content.
      // Unlike body.insertOoxml(), this method replaces ALL document parts
      // including comments, headers, footers, styles, etc.
      //
      // The options are set to minimize side effects - we want to replace
      // content only, not change the document's theme, styles, or other settings.
      context.document.insertFileFromBase64(base64Docx, Word.InsertLocation.replace, {
        importTheme: false,
        importStyles: false,
        importParagraphSpacing: false,
        importPageColor: false,
        importChangeTrackingMode: false,
        importCustomProperties: false,
        importCustomXmlParts: false,
        importDifferentOddEvenPages: false,
      });
      await context.sync();
    });
  }

  async queryRange(params: QueryRangeParams) {
    // Use SyncEngine which now uses a persistent context.
    // This ensures all tracked ranges are created in the same Word.RequestContext,
    // allowing cross-range operations like SpanTo and trackedRange anchors to work.
    return this.syncEngine.perform(new QueryRangeOperation(params));
  }

  async selectRange({ trackedRangeId }: { trackedRangeId: string }): Promise<{
    selected: boolean;
    text: string;
    length: number;
  }> {
    const range = getTrackedRange(trackedRangeId);

    if (!range) {
      throw new Error(
        `Cannot find the tracked range "${trackedRangeId}". ` +
          "The range may have expired, the document was closed, or the add-in was reloaded.",
      );
    }

    // Use Word.run with the tracked range to ensure it's valid in a new context
    return Word.run(range, async (context) => {
      // Load text property before reading
      range.load("text");
      await context.sync();

      // Select the range - this will scroll to and highlight it
      range.select();
      await context.sync();

      return {
        selected: true,
        text: range.text,
        length: range.text.length,
      };
    });
  }

  async devEval({ code }: DevEvalParams): Promise<DevEvalResult> {
    const logs: string[] = [];
    const log = (...args: unknown[]) => {
      logs.push(
        args
          .map((arg) => {
            if (arg === null) return "null";
            if (arg === undefined) return "undefined";
            if (typeof arg === "object") {
              try {
                return JSON.stringify(arg, null, 2);
              } catch {
                return String(arg);
              }
            }
            return String(arg);
          })
          .join(" "),
      );
    };

    try {
      const result = await Word.run(async (context) => {
        // Create an async function from the code string
        // The function has access to: context, Word, log
        const AsyncFunction = Object.getPrototypeOf(async function () {}).constructor;
        const fn = new AsyncFunction("context", "Word", "log", code);
        return await fn(context, Word, log);
      });

      // Ensure result is JSON-serializable
      let serializedResult: unknown;
      try {
        serializedResult = JSON.parse(JSON.stringify(result));
      } catch {
        serializedResult = String(result);
      }

      return { result: serializedResult, logs };
    } catch (e) {
      const error = e instanceof Error ? e.message : String(e);
      return { error, logs };
    }
  }

  async storeConversationId({
    workspaceId,
    conversationId,
  }: {
    workspaceId: string;
    conversationId: string | null;
  }): Promise<void> {
    storeConversationId(workspaceId, conversationId);
  }
}
